import { useRef, useState } from 'react';
import { Upload, Scissors, Download, AlertCircle, Trash2, X } from 'lucide-react';
import { pb, formatWhen, errorText } from '../lib/pb';
import { useCollection } from '../lib/hooks';
import { TRANSITIONS, SFX_OPTIONS, type StitchJob } from '../../../shared/types';
import { confirmDialog } from './ConfirmDialog';

const TRANSITION_LABELS: Record<string, string> = {
  cut: 'Hard cut',
  fade: 'Fade',
  dissolve: 'Dissolve',
  wipeleft: 'Wipe left',
  wiperight: 'Wipe right',
  wipeup: 'Wipe up',
  wipedown: 'Wipe down',
  slideleft: 'Slide left',
  slideright: 'Slide right',
  slideup: 'Slide up',
  slidedown: 'Slide down',
  circleopen: 'Circle open',
  circleclose: 'Circle close',
  distance: 'Zoom blend',
};

const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-stone-100 text-natural-text/60',
  processing: 'bg-natural-sage/15 text-natural-sage',
  done: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-600',
};

/**
 * Video Stitcher.
 *
 * Joins a hook clip to a main clip. The work is a queued job in the worker like
 * everything else, so progress is real rather than an animated bar — v1 showed
 * a fake progress indicator while a synchronous request blocked.
 */
export default function VideoCombiner() {
  const [hook, setHook] = useState<File | null>(null);
  const [main, setMain] = useState<File | null>(null);
  const [transition, setTransition] = useState<string>('fade');
  const [speed, setSpeed] = useState(0.5);
  const [sfx, setSfx] = useState<string>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hookInput = useRef<HTMLInputElement>(null);
  const mainInput = useRef<HTMLInputElement>(null);

  const jobs = useCollection<StitchJob>('stitch_jobs', { sort: '-created' });

  const submit = async () => {
    if (!hook || !main) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('user', pb.authStore.record?.id ?? '');
      form.append('hook', hook);
      form.append('main', main);
      form.append('transition', transition);
      form.append('transition_speed', String(speed));
      form.append('sfx', sfx);
      form.append('status', 'queued');

      // Creating the record queues the job — a hook does that server-side, so
      // there is no window where an upload exists but nothing is going to run it.
      await pb.collection('stitch_jobs').create(form);

      setHook(null);
      setMain(null);
      if (hookInput.current) hookInput.current.value = '';
      if (mainInput.current) mainInput.current.value = '';
      await jobs.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className="zen-card p-6 space-y-5">
        <div>
          <h2 className="font-serif text-lg font-medium">Stitch two clips</h2>
          <p className="text-xs text-natural-text/55 mt-0.5">
            The hook plays first, then the main clip. Both are matched to the main clip's format.
          </p>
        </div>

        <FilePicker
          label="Hook clip"
          file={hook}
          inputRef={hookInput}
          onPick={setHook}
          onClear={() => {
            setHook(null);
            if (hookInput.current) hookInput.current.value = '';
          }}
        />

        <FilePicker
          label="Main clip"
          file={main}
          inputRef={mainInput}
          onPick={setMain}
          onClear={() => {
            setMain(null);
            if (mainInput.current) mainInput.current.value = '';
          }}
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="zen-label">Transition</label>
            <select
              value={transition}
              onChange={(e) => setTransition(e.target.value)}
              className="zen-input"
            >
              {TRANSITIONS.map((t) => (
                <option key={t} value={t}>
                  {TRANSITION_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="zen-label">Sound effect</label>
            <select value={sfx} onChange={(e) => setSfx(e.target.value)} className="zen-input">
              {SFX_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === 'none' ? 'None' : s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {transition !== 'cut' && (
          <div>
            <label className="zen-label">Overlap — {speed.toFixed(2)}s</label>
            <input
              type="range"
              min={0.1}
              max={2}
              step={0.05}
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="w-full accent-[var(--color-natural-sage)]"
            />
            <p className="text-[11px] text-natural-text/45 mt-1">
              Clamped to fit if either clip is shorter than this.
            </p>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span className="break-words">{error}</span>
          </div>
        )}

        <button onClick={submit} disabled={!hook || !main || busy} className="zen-btn-primary w-full">
          <Scissors size={15} />
          {busy ? 'Uploading…' : 'Stitch'}
        </button>
      </div>

      <div className="zen-card p-6 space-y-4">
        <h2 className="font-serif text-lg font-medium">Your stitches</h2>

        {jobs.loading && <p className="text-xs text-natural-text/40">Loading…</p>}
        {!jobs.loading && jobs.items.length === 0 && (
          <p className="text-xs text-natural-text/40 py-6 text-center">Nothing stitched yet.</p>
        )}

        <div className="space-y-3 max-h-[600px] overflow-y-auto -mr-2 pr-2">
          {jobs.items.map((job) => (
            <JobCard key={job.id} job={job} onDeleted={() => void jobs.reload()} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilePicker({
  label,
  file,
  inputRef,
  onPick,
  onClear,
}: {
  label: string;
  file: File | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (file: File) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <label className="zen-label">{label}</label>
      {file ? (
        <div className="flex items-center gap-2 px-4 py-2.5 border border-natural-sage/50 bg-natural-sage/5 rounded-xl">
          <span className="text-xs truncate flex-1">{file.name}</span>
          <span className="text-[10px] text-natural-text/45 shrink-0 font-mono">
            {(file.size / 1024 / 1024).toFixed(1)} MB
          </span>
          <button
            onClick={onClear}
            className="p-1 rounded text-natural-text/40 hover:text-red-500 cursor-pointer shrink-0"
          >
            <X size={13} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 px-4 py-6 border border-dashed
                     border-natural-border rounded-xl text-xs text-natural-text/50
                     hover:border-natural-sage/50 hover:text-natural-sage transition-colors cursor-pointer"
        >
          <Upload size={15} />
          Choose a video
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) onPick(picked);
        }}
        className="hidden"
      />
    </div>
  );
}

function JobCard({ job, onDeleted }: { job: StitchJob; onDeleted: () => void }) {
  const [showLog, setShowLog] = useState(false);
  const resultUrl = job.result ? pb.files.getURL(job as never, job.result) : '';

  return (
    <div className="border border-natural-border rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className={`zen-pill ${STATUS_STYLE[job.status] ?? ''}`}>
          {job.status === 'processing' && (
            <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
          )}
          {job.status}
        </span>
        <span className="text-[10px] text-natural-text/40">{formatWhen(job.created)}</span>
      </div>

      <p className="text-[11px] text-natural-text/55">
        {TRANSITION_LABELS[job.transition] ?? job.transition}
        {job.transition !== 'cut' && ` · ${Number(job.transition_speed).toFixed(2)}s`}
        {job.sfx && job.sfx !== 'none' && ` · ${job.sfx}`}
      </p>

      {job.status === 'done' && resultUrl && (
        <>
          <video src={resultUrl} controls playsInline preload="metadata" className="w-full rounded-xl bg-stone-900" />
          <a href={resultUrl} download className="zen-btn-primary w-full py-2 text-xs">
            <Download size={13} />
            Download
          </a>
        </>
      )}

      {job.status === 'failed' && job.error && (
        <p className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 break-words">
          {job.error}
        </p>
      )}

      <div className="flex items-center gap-2">
        {job.log && (
          <button
            onClick={() => setShowLog((v) => !v)}
            className="text-[10px] text-natural-text/50 hover:text-natural-sage cursor-pointer"
          >
            {showLog ? 'Hide log' : 'Show log'}
          </button>
        )}
        <button
          onClick={async () => {
            if (
              !(await confirmDialog({
                title: 'Delete this stitch?',
                message: 'The uploaded clips and the stitched result are removed.',
                confirmLabel: 'Delete',
                danger: true,
              }))
            ) {
              return;
            }
            await pb.collection('stitch_jobs').delete(job.id);
            onDeleted();
          }}
          className="ml-auto p-1 rounded text-natural-text/35 hover:text-red-500 cursor-pointer"
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {showLog && job.log && (
        <pre className="text-[10px] font-mono bg-stone-50 border border-natural-border rounded-lg p-2 overflow-x-auto whitespace-pre-wrap">
          {job.log}
        </pre>
      )}
    </div>
  );
}
